// @flow

import React from 'react';
import { get, partial } from 'lodash';

import { Screen } from '@zapier/theme';
import {
  type ApiKeys,
  type PaymentSource,
  type PaymentSourceMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';
import {
  Button,
  ButtonGroup,
  FlexLayout,
  Link,
  Media,
  Text,
} from '@zapier/common-components';
import { PaymentMethodImage, PaypalButton, PaypalInfo } from '@zapier/payment';

import PaymentMethod from '../PaymentMethod';
import PaymentMethodSelect from '../PaymentMethodSelect';

type Props = {
  /** Payment backend API keys */
  apiKeys: ApiKeys,
  /** Currently select method */
  currentMethod?: PaymentSourceMethod,
  /** Hide switch component button */
  hideSwitch: boolean,
  /** Show editor */
  isEditing: boolean,
  /** Call to canel edit */
  onCancelEdit: () => void,
  /** Call to start edit */
  onEdit: () => void,
  /** Call when editor throws an error */
  onErrorEdit: Error => void,
  /** Call to save current edit */
  onSaveEdit: () => void,
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceMethod) => void,
  /** User's currently saved paypal details */
  savedSource: ?PaymentSource,
  /** Shows the new select dropdown UI */
  showSelect: boolean,
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: PaymentSourceMethod[],
};

const renderLabel = (props: Props) =>
  props.savedSource && !props.isEditing ? (
    <PaypalInfo paypal={get(props, ['savedSource', 'details'])} />
  ) : (
    <React.Fragment>
      <PaymentMethodImage
        imageType={paymentSourceMethods.paypal}
        isSmall={true}
      />
      <h3>
        <Text type="section-head">Connect PayPal</Text>
      </h3>
    </React.Fragment>
  );

const renderAside = (props: Props) => (
  <Media queries={{ small: Screen.queries.phoneOnly }}>
    {({ small }) => {
      if (!props.isEditing) {
        return (
          <Button
            color="secondary"
            label="Edit Payment Method"
            onClick={props.onEdit}
          >
            {props.savedSource
              ? `Edit${!small ? ' Payment Method' : ''}`
              : 'Add'}
          </Button>
        );
      } else {
        const switchUI = props.showSelect ? (
          <PaymentMethodSelect
            currentMethod={props.currentMethod}
            onChange={props.onSelectSource}
            supportedMethods={props.supportedMethods}
          />
        ) : (
          <Button
            backgroundType="clear"
            color="primary"
            label="Switch to Card"
            onClick={partial(
              props.onSelectSource,
              paymentSourceMethods.card,
              true
            )}
          >
            Switch to Card
          </Button>
        );
        return props.hideSwitch ? null : switchUI;
      }
    }}
  </Media>
);

const PaymentMethodPaypal = (props: Props) => (
  <PaymentMethod
    isEditing={props.isEditing}
    renderAside={partial(renderAside, props)}
    renderLabel={partial(renderLabel, props)}
  >
    <FlexLayout align="center" direction="column" gutter="normal">
      <Text type="body">
        {props.savedSource
          ? 'You can reconnect to use a different PayPal account.'
          : 'Please connect your PayPal account.'}
      </Text>
      <ButtonGroup
        direction="row"
        isFlex={true}
        propsQueries={{ phoneOnly: { direction: 'column', isReversed: true } }}
      >
        <Button
          color="secondary"
          isMinWidth={true}
          label="Cancel"
          onClick={props.onCancelEdit}
        >
          Cancel
        </Button>
        <Media queries={{ small: Screen.queries.phoneOnly }}>
          {({ small }) => (
            <PaypalButton
              apiKey={props.apiKeys.braintree}
              margin={small ? 'bottom' : 'left'}
              onAuthorize={props.onSaveEdit}
              onError={props.onErrorEdit}
            />
          )}
        </Media>
      </ButtonGroup>
      <Link
        href="https://www.paypal.com/cgi-bin/webscr?cmd=xpt/Marketing/popup/OLCWhatIsPayPal-outside"
        target="_blank"
      >
        What is PayPal?
      </Link>
    </FlexLayout>
  </PaymentMethod>
);

export default PaymentMethodPaypal;
